import { useState, useCallback, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import clsx from 'clsx'
import Button from '../ui/buttons/Button'
import IconDownload from '../ui/icons/IconDownload'
import TextHeadline from '../ui/texts/TextHeadline'
import IconCalendar from '../ui/icons/IconCalendar'

type Slide = {
    gradient: string
    icon: string
    powerupName: string
    title: string
    description: string
    textColorClass: string
}

const SLIDES: Slide[] = [
    {
        gradient: 'from-[#602bfe] to-[#963aff]',
        icon: '/images/powerups/PowerUp_Star_Shift.png',
        powerupName: 'Stern',
        title: 'Berufsunfähigkeit',
        description: 'Unverwundbarkeit im Spiel, direkte Hilfe wenn der Job nicht mehr geht.',
        textColorClass: 'text-white',
    },
    {
        gradient: 'from-[#006aff] to-[#599eff]',
        icon: '/images/powerups/PowerUp_Shield_Shift.png',
        powerupName: 'Schild',
        title: 'Grundfähigkeit',
        description: 'Damit sichert ihr die wichtigsten Fähigkeiten im Leben.',
        textColorClass: 'text-white',
    },
    {
        gradient: 'from-[#ff7300] to-[#ffb001]',
        icon: '/images/powerups/PowerUp_Coin_Shift.png',
        powerupName: 'Münzen',
        title: 'Vorsorge',
        description: 'Du sparst heute – dein Kind startet morgen mit mehr.',
        textColorClass: 'text-black-stroke',
    },
    {
        gradient: 'from-[#00bd6e] to-[#c4eb16]',
        icon: '/images/powerups/PowerUp_Batterie_Shift.png',
        powerupName: 'Blitz',
        title: 'Unfallschutz',
        description: 'Greift sofort bei unerwarteten Unfällen – immer und überall.',
        textColorClass: 'text-black-stroke',
    },
]

const AUTO_ADVANCE_MS = 4000
const SWIPE_THRESHOLD_PX = 40

type ParentsInformationalOverlayProps = {
    visible: boolean
    onAppointment: () => void
    onSaveCard: () => void
}

export default function ParentsInformationalOverlay({
    visible,
    onAppointment,
    onSaveCard,
}: ParentsInformationalOverlayProps) {
    const [currentSlide, setCurrentSlide] = useState(0)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const dragStartX = useRef<number | null>(null)
    const isDragging = useRef(false)

    const startTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current)
        timerRef.current = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % SLIDES.length)
        }, AUTO_ADVANCE_MS)
    }, [])

    useEffect(() => {
        if (!visible) {
            if (timerRef.current) clearInterval(timerRef.current)
            return
        }
        setCurrentSlide(0)
        startTimer()
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [visible, startTimer])

    const goToSlide = useCallback((index: number) => {
        setCurrentSlide(index)
        startTimer()
    }, [startTimer])

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        dragStartX.current = e.clientX
        isDragging.current = true
    }, [])

    const finishDrag = useCallback((clientX: number) => {
        if (!isDragging.current || dragStartX.current === null) return
        isDragging.current = false
        const delta = clientX - dragStartX.current
        dragStartX.current = null
        if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return
        setCurrentSlide(prev =>
            delta < 0
                ? Math.min(prev + 1, SLIDES.length - 1)
                : Math.max(prev - 1, 0)
        )
        startTimer()
    }, [startTimer])

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        finishDrag(e.clientX)
    }, [finishDrag])

    const handlePointerCancel = useCallback(() => {
        isDragging.current = false
        dragStartX.current = null
    }, [])

    return (
        <AnimatePresence>
            <motion.div className='top-20 absolute flex flex-col justify-center items-center w-full'>
                <TextHeadline colorPreset='red' fontSize={24} maxCharsPerLine={24}>Dein Kind hat's ins Ziel geschafft und Diese Karte freigespielt!</TextHeadline>
            </motion.div>
            {visible ? (
                <motion.div
                    className={clsx(
                        'relative flex flex-col items-center pointer-events-auto pattern-background',
                        'bg-white-gradient drop-shadow-2-white-stroke border border-white-stroke',
                        'px-4 py-4 rounded-3xl w-full max-w-xs gap-4',
                    )}
                    initial={{ y: '110%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '110%', opacity: 0 }}
                    transition={{
                        type: 'spring',
                        stiffness: 360,
                        damping: 30,
                        mass: 0.85,
                    }}
                >
                    {/* ── Slider ── */}
                    <div
                        className='overflow-hidden w-full cursor-grab active:cursor-grabbing select-none'
                        onPointerDown={handlePointerDown}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerCancel}
                        onPointerLeave={handlePointerCancel}
                    >
                        <motion.div
                            className='flex'
                            animate={{ x: `-${currentSlide * 100}%` }}
                            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
                        >
                            {SLIDES.map((slide, i) => (
                                <div key={i} className='flex-none w-full px-px'>
                                    <div
                                        className={clsx(
                                            'flex gap-2 items-start p-4 rounded-xl w-full bg-gradient-to-b',
                                            slide.gradient,
                                        )}
                                    >
                                        <div className='relative shrink-0 size-12 overflow-hidden'>
                                            <img
                                                src={slide.icon}
                                                alt=''
                                                aria-hidden
                                                draggable={false}
                                                className='absolute inset-0 w-full h-full object-contain'
                                            />
                                        </div>
                                        <div className={clsx('flex flex-1 flex-col gap-2 text-[12px]', slide.textColorClass)}>
                                            <p className='font-nunito font-black leading-[0.8]'>{slide.powerupName}</p>
                                            <p className='font-nunito font-black leading-[0.8]'>{slide.title}</p>
                                            <p className='font-nunito font-bold leading-[1.3]'>{slide.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    {/* ── Pagination dots ── */}
                    <div className='flex items-center gap-2.5' aria-hidden>
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                type='button'
                                onClick={() => goToSlide(i)}
                                className={clsx(
                                    'rounded-full h-2 transition-all duration-300 pointer-events-auto',
                                    i === currentSlide
                                        ? 'w-4 bg-white-stroke shadow-[inset_0px_1px_0px_0px_var(--white-stroke-even-darker,#06487c)]'
                                        : 'w-2 bg-white-stroke shadow-[inset_0px_1px_0px_0px_var(--white-stroker-darker,#5486ac)]',
                                )}
                                aria-label={`Folie ${i + 1}`}
                            />
                        ))}
                    </div>

                    {/* ── Body text ── */}
                    <div className='flex flex-col gap-1 w-full'>
                        <p className='text-white-stroke-even-darker font-nunito font-bold text-[16px] text-center leading-[22px] tracking-[-0.32px]'>
                            Im Spiel hat es sich geschützt, vorgesorgt und jede Herausforderung bewältigt.
                        </p>
                        <p className='text-white-stroke-even-darker font-nunito font-black text-[16px] text-center leading-[22px] tracking-[-0.32px]'>
                            Wie das im echten Leben gelingt, erfährst du bei einem Termin in deiner Sparkasse. Die echte Sammelkarte kannst du dort direkt mitnehmen.
                        </p>
                    </div>

                    {/* ── Buttons ── */}
                    <div className='flex flex-col gap-2 w-full'>
                        <Button color='orange' size='large' onClick={onAppointment} multiline={true} maxCharsPerLine={28} icon={<IconCalendar width='100%' height='100%' fill='#5486AC' />}>
                            Termin vereinbaren &amp; Sammelkarte Abholen
                        </Button>
                        <Button color='white' icon={<IconDownload fill='#5486AC' />} onClick={onSaveCard}>
                            Karte Speichern
                        </Button>
                        <p className='text-white-stroke-even-darker font-nunito text-[12px] text-center leading-[1.25] tracking-[-0.24px]'>
                            <span className='font-black'>Wichtig:</span>
                            {' Karte beim Termin digital vorzeigen.'}
                        </p>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}
